/**
 * Comment Extension(s) for TipTap (T-057, frontend half)
 *
 * Two node types, because a Mark cannot wrap a collapsed selection:
 *   - CommentMark: applied to a text RANGE (selection with from !== to).
 *   - CommentMarker: an inline atom inserted at a cursor POINT (collapsed
 *     selection) — a clickable 💬 marker.
 *
 * Both render a `data-comment-id` attribute and share a click-to-open
 * behavior: clicking either dispatches a window CustomEvent
 * ('openCommentThread') that CommentsPanel listens for.
 *
 * NOT registered here — the architect wires CommentMark/CommentMarker into
 * ContinuousDocument's `extensions:` array.
 */

import { Mark, Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';

export const OPEN_COMMENT_THREAD_EVENT = 'openCommentThread';

function dispatchOpenThread(commentId) {
  if (commentId === null || commentId === undefined) return;
  window.dispatchEvent(new CustomEvent(OPEN_COMMENT_THREAD_EVENT, { detail: { commentId } }));
}

// Reason: one click handler covers both the mark's <span> and the marker's
// <sup> — ProseMirror runs handleClick for every plugin regardless of which
// node was hit, so registering this on CommentMark alone is enough even if
// the click lands on a CommentMarker atom. Kept on both extensions anyway so
// either one works standalone if the architect ever registers just one.
function commentClickPlugin(pluginName) {
  return new Plugin({
    key: new PluginKey(pluginName),
    props: {
      handleClick(view, pos, event) {
        const el = event.target && event.target.closest && event.target.closest('[data-comment-id]');
        if (!el) return false;
        // A commented IMAGE is wrapped by this mark span. Let the click fall
        // through so ProseMirror selects the image node (→ resize handles, which
        // render amber to flag the comment); the thread opens from its card.
        if (el.querySelector && el.querySelector('img, .resizable-image-wrapper')) return false;
        // #2: a resolved comment no longer opens the panel on click — it only
        // reacts to hover from its card in the "Resueltos" group (#3).
        if (el.getAttribute('data-resolved') === 'true') return false;
        dispatchOpenThread(el.getAttribute('data-comment-id'));
        return true;
      },
    },
  });
}

/**
 * CommentMark — wraps a selected text range with a comment anchor.
 *
 * Commands:
 *   setComment(commentId)     — apply the mark to the current selection
 *   unsetComment()            — remove the mark from the current selection
 *   removeCommentById(id)     — remove the mark wherever it appears in the doc
 *   setCommentResolved(id, resolved) — best-effort: flag the mark's `resolved`
 *     attribute (drives the `.resolved` CSS class) when a thread is
 *     resolved/reopened.
 */
export const CommentMark = Mark.create({
  name: 'comment',
  // Reason: inclusive:false so typing right after the marked text does NOT
  // extend the comment onto new characters.
  inclusive: false,

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-comment-id'),
        renderHTML: (attributes) => (attributes.commentId ? { 'data-comment-id': attributes.commentId } : {}),
      },
      resolved: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-resolved') === 'true',
        renderHTML: (attributes) => (attributes.resolved ? { 'data-resolved': 'true' } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-comment-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const resolved = HTMLAttributes['data-resolved'] === 'true';
    const className = `tiptap-comment${resolved ? ' resolved' : ''}`;
    return ['span', mergeAttributes(HTMLAttributes, { class: className }), 0];
  },

  addCommands() {
    return {
      setComment:
        (commentId) =>
        ({ commands }) =>
          commands.setMark(this.name, { commentId }),

      unsetComment:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),

      removeCommentById:
        (commentId) =>
        ({ tr, state, dispatch }) => {
          let found = false;
          state.doc.descendants((node, pos) => {
            node.marks.forEach((mark) => {
              if (mark.type.name === 'comment' && String(mark.attrs.commentId) === String(commentId)) {
                found = true;
                tr.removeMark(pos, pos + node.nodeSize, mark.type);
              }
            });
          });
          if (found && dispatch) dispatch(tr);
          return found;
        },

      setCommentResolved:
        (commentId, resolved) =>
        ({ tr, state, dispatch }) => {
          let found = false;
          state.doc.descendants((node, pos) => {
            const mark = node.marks.find(
              (m) => m.type.name === 'comment' && String(m.attrs.commentId) === String(commentId)
            );
            if (mark) {
              found = true;
              tr.addMark(pos, pos + node.nodeSize, mark.type.create({ ...mark.attrs, resolved }));
            }
            // Node-level anchors (image / whole table) store commentId as an attr.
            if (node.attrs && node.attrs.commentId != null && String(node.attrs.commentId) === String(commentId)) {
              found = true;
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, commentResolved: resolved });
            }
          });
          if (found && dispatch) dispatch(tr);
          return found;
        },
    };
  },

  addProseMirrorPlugins() {
    return [commentClickPlugin('commentMarkClick')];
  },
});

/**
 * CommentMarker — clickable 💬 atom for comments anchored at a cursor point
 * (collapsed selection has no range for a mark to wrap).
 *
 * Commands:
 *   insertCommentMarker(commentId) — insert the marker at the current cursor
 */
export const CommentMarker = Node.create({
  name: 'commentMarker',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      commentId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-comment-id'),
        renderHTML: (attributes) => (attributes.commentId ? { 'data-comment-id': attributes.commentId } : {}),
      },
      resolved: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-resolved') === 'true',
        renderHTML: (attributes) => (attributes.resolved ? { 'data-resolved': 'true' } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'sup[data-comment-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const resolved = HTMLAttributes['data-resolved'] === 'true';
    const className = `tiptap-comment-marker${resolved ? ' resolved' : ''}`;
    return ['sup', mergeAttributes(HTMLAttributes, { class: className, contenteditable: 'false' }), '💬'];
  },

  addCommands() {
    return {
      insertCommentMarker:
        (commentId) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { commentId } }),
    };
  },

  addProseMirrorPlugins() {
    return [commentClickPlugin('commentMarkerClick')];
  },
});

export default { CommentMark, CommentMarker };
